import { SESSION_STATUS } from '@/services/sessions';

export enum CHAT_USER_ROLE {
  USER = 'user',
  AGENT = 'agent',
}

export enum CHAT_CHANNEL_TYPE {
  BOT = 'bot',
  HUMAN = 'human',
}

export enum CHAT_TYPE {
  DIRECT = 'direct',
  GROUP = 'group',
}

export enum CHAT_MESSAGE_RATING {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  CLEAR = 'clear',
}

export interface Chat {
  id: string;
  created_at: number | null;
  type: CHAT_TYPE;
  name: string;
  description: string;
  participants_id: {
    profile_picture: {
      url: string;
    } | null;
  }[];
  group_image: {
    access: string;
    path: string;
    name: string;
    type: string;
    size: number;
    mime: string;
    meta: {
      width: number;
      height: number;
    };
    url: string;
  } | null;
}

export interface Message {
  id: number;
  created_at: number;
  channel_id: string;
  sender_id: number;
  text: string;
  receivers: {
    user_id: string;
    read: boolean;
  }[];
  _sender_info?: {
    username: string;
    profile_picture: {
      url: string;
    } | null;
  };
  date?: string;
}

export interface Contact {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string | null;
  profile_picture: {
    url: string;
  } | null;
}

export interface ChatRefreshResponse {
  updates: {
    channel_id: string;
    last_message: {
      created_at: number;
      sender_id: string;
      text: string;
    };
    _channel_info: Chat;
  }[];
  current_channel?: {
    channel_info: {
      _channel_info: Chat;
    };
    messages: {
      date: string;
      messages: Message[];
    }[];
  };
  contact_list: Contact[];
}

export type AgentThinkingStepKind = 'status' | 'tool';

export type AgentThinkingStepState = 'running' | 'done';

export interface AgentThinkingStep {
  id: string;
  kind: AgentThinkingStepKind;
  label: string;
  state: AgentThinkingStepState;
  toolName?: string;
  toolUseId?: string;
  startedAt: number;
  endedAt?: number;
}

/**
 * A Library Document a Connie turn searched or read, with the pages it read and the
 * pages its answer cites (FSI #1159). Ids, names and page numbers only.
 */
export interface LibraryReference {
  documentId: string;
  name: string;
  pagesRead: number[];
  cited: number[];
}

/**
 * A Library search of a live Connie turn, for the working panel: the documents it
 * found, placed after the N-th closed progress note. Not saved with the session.
 */
export interface LibrarySearch {
  afterNotes: number;
  documents: { documentId: string; name: string }[];
}

export interface ChatMessage {
  id: string;
  rating: CHAT_MESSAGE_RATING | null;
  type: CHAT_CHANNEL_TYPE;
  content: string;
  timestamp: string;
  tools?: string[];
  isLoader?: boolean;
  isStreaming?: boolean;
  isLiveStream?: boolean;
  streamStatus?: string;
  toolActivity?: {
    toolName: string;
    toolUseId: string;
  };
  toolCallOutputs?: {
    toolName: string;
    toolUseId: string;
    output: unknown;
  }[];
  thinkingSteps?: AgentThinkingStep[];
  /**
   * Latest `progress` note while tools run. Cleared when the answer
   * starts. Not saved with the session.
   */
  progressStatus?: string;
  /**
   * Progress notes from this live turn, in order. History reloads
   * do not restore them.
   */
  progressNotes?: string[];
  /** Library searches of this live turn, in order. History reloads do not restore them. */
  librarySearches?: LibrarySearch[];
  /** FSI's index for this turn in the session, for citation excerpts. */
  turnIndex?: number;
  /** Library Documents the turn touched, from the done event or history. */
  libraryReferences?: LibraryReference[];
}

export interface Session {
  id: string;
  created_at: number;
  user?: number;
  agent?: number;
  title: string;
  description: string;
  updated_at?: number;
  BCjobID: string;
  BCcontactID?: string;
  is_archived: boolean;
  status: SESSION_STATUS;
  last_message: string;
  last_message_sender_type: CHAT_USER_ROLE;
  last_message_timestamp: number;
  /** Connie session turn count from list API. */
  turnCount?: number;
  /** Connie: LLM title not ready yet; UI may refetch shortly. */
  titlePending?: boolean;
  _agent: {
    name: string;
    avatar: {
      url: string;
    };
  };
}

export interface SuggestionGroup {
  label: string;
  icon: React.ElementType;
  highlight: string;
  items: string[];
}
